<template>
  <v-col
    :class="`col-12 main-content pa-0 ${backgroundColor}`"
  >
    <v-row
      v-if="showMessage"
      class="ma-0"
    >
      <error-unknown :status-message="currentStatusMessage" />
    </v-row>

    <v-dialog
      v-model="isLoadingMainContent"
      class="ma-0 fill-height"
      hide-overlay
      max-width="350px"
    >
      <v-card>
        <v-card-text class="pa-4">
          <v-col>
            <v-row
              class="fill-height my-4"
              align="center"
              justify="center"
            >
              <message-view />
            </v-row>
          </v-col>
        </v-card-text>
      </v-card>
    </v-dialog>

    <keep-alive>
      <component
        :is="currentComponentName"
        v-if="showContent"
      />
    </keep-alive>
  </v-col>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'

import render from 'src/utils/render'

const { mapGetters: mapAuthGetters } = createNamespacedHelpers('auth')
const { mapGetters: mapLayoutGetters } = createNamespacedHelpers('layout')
const { mapActions: mapMessagesActions, mapGetters: mapMessagesGetters } = createNamespacedHelpers('messages')
const { mapActions: mapToolsActions, mapGetters: mapToolsGetters, mapState: mapToolsState } = createNamespacedHelpers('tools')

// noinspection ES6MissingAwait
export default {
  name: 'MainContent',
  components: {
    AdxAndOpenBidding: () => import('src/programmatic/views/AdxAndOpenBidding'),
    AdxHealth: () => import('src/programmatic/views/AdxHealth'),
    Auth: () => import('src/auth/views/Auth'),
    Backlogs: () => import('src/docs/views/Backlogs'),
    Creatives: () => import('src/admanager/views/Creatives'),
    CustomAnalytics: () => import('src/custom-analytics/views/CustomAnalytics'),
    Dashboard: () => import('src/dashboard/views/Dashboard'),
    DataRefresh: () => import('src/docs/views/DataRefresh'),
    Deprecation: () => import('src/docs/views/Deprecation'),
    Develop: () => import('src/develop/views/Develop'),
    DevTools: () => import('@/develop/views/DevTools'),
    Delivery: () => import('src/delivery/views/Delivery'),
    Docs: () => import('src/docs/views/Docs'),
    Discrepancy: () => import('src/delivery/views/Discrepancy'),
    ErrorUnknown: () => import('src/messages/components/ErrorUnknown'),
    Home: () => import('src/home/views/Home'),
    MessageView: () => import('src/messages/components/MessageView'),
    LineItems: () => import('src/admanager/views/LineItems'),
    NetworkHealth: () => import('src/programmatic/views/NetworkHealth'),
    Networks: () => import('src/admanager/views/Networks'),
    Organization: () => import('src/internal/views/Organization'),
    Orders: () => import('src/admanager/views/Orders'),
    TimeRollup: () => import('src/internal/views/Overview'),
    Pacing: () => import('src/delivery/views/Pacing'),
    Person: () => import('src/internal/views/Person'),
    PrivacyPolicy: () => import('src/docs/views/PrivacyPolicy'),
    Project: () => import('src/internal/views/Project'),
    Releases: () => import('src/docs/views/Releases'),
    SummaryCardBar: () => import('src/tools/components/summary/SummaryCardBar'),
    TimeSheet: () => import('src/internal/views/Time'),
    Whitelisting: () => import('src/docs/views/Whitelisting'),
  },
  data() {
    return {
      currentReportCriteria: null,
      moduleMap: {
        'custom-analytics': 'customAnalytics',
      },
      pendingHydration: false,
    }
  },
  computed: {
    ...mapAuthGetters(['inDemoMode', 'isInternalUser']),
    ...mapLayoutGetters(['backgroundColor']),
    ...mapMessagesGetters(['currentMessage', 'messageViews', 'statusCodes']),
    ...mapToolsGetters([
      'currentComponentName',
      'currentRouteName',
      'currentSelectedReport',
      'hasNoReport',
      'isAssociationIdRequiredOnRoute',
      'isHomeRoute',
      'isLoadingContent',
      'selectedSearch',
      'teamsByReportCode',
    ]),
    ...mapToolsState({
      accessFields: (state) => state.accessFields,
      associationId: (state) => state.associationId,
      baseReportName: (state) => state.baseReportName,
      query: (state) => state.query,
      reportPath: (state) => state.reportPath,
      routeName: (state) => state.routeName,
      subId: (state) => state.subId,
    }),
    storeName() {
      const { moduleMap, routeName } = this
      const storeName = routeName || this.$route.name
      return moduleMap[storeName] || storeName
    },
    isStoreRegistered() {
      return this.$store.hasModule(this.storeName)
    },
    columns() {
      return this.isStoreRegistered && this.$store.state[this.storeName]?.columns
    },
    isLoadingMainContent: {
      get() {
        const { isLoadingContent, isStoreRegistered } = this
        const isPendingStore = () => !isStoreRegistered && 'store'

        return isLoadingContent || isPendingStore()
      },
      set(value) {
        this.$store.commit('tools/isLoadingMainContent', value)
      },
    },
    showContent() {
      return true
    },
    showMessage() {
      return (this.hasNoReport && !this.isHomeRoute) || (!this.showContent && !this.isLoading)
    },
    currentStatusMessage() {
      return this.hasNoReport ? `No Report Available for ${this.selectedSearch?.text || 'Selected'}` : 'Error Unknown'
    },
  },
  watch: {
    isLoadingMainContent: {
      handler(newValue, oldValue) {
        if (newValue && newValue !== oldValue) {
          this.isLoadingMainContent = newValue
        }
      },
      immediate: true,
    },
    inDemoMode() {
      this.setupAndRenderView()
    },
    currentRouteName(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.registerStore()
      }
    },
    selectedSearch: {
      deep: true,
      handler(newValue, oldValue) {
        const requireHydration = () => !this.pendingHydration && this.hasChangedSelectedOption(newValue, oldValue)

        if (this.isAssociationIdRequiredOnRoute && requireHydration()) {
          this.pendingHydration = true
          this.setupAndRenderView().then(() => {
            this.pendingHydration = false
          })
        }
      },
    },
    $route(newValue, oldValue) {
      const requireHydration = () => !this.pendingHydration && this.hasChangedRoute(newValue, oldValue)

      if (requireHydration()) {
        if (!this.pendingHydration) {
          this.pendingHydration = true
          this.setupAndRenderView().then(() => {
            this.pendingHydration = false
          })
        }
      }
    },
  },
  created() {
    this.setup()
  },
  methods: {
    ...mapMessagesActions(['showLoadingMessage', 'showStatusMessage']),
    ...mapToolsActions(['getParams', 'isInvalidRequest']),

    setup() {
      if (this.isAssociationIdRequiredOnRoute) {
        const { params, query } = this.$route
        const reportPath = params.reportPath || query.reportPath
        const associationId = params.associationId || query.associationId

        if (reportPath && (associationId !== 'all-associations' || params.routeName === 'internal')) {
          this.setupView() // The watch on selectedSearch will handle the render.
        } else {
          this.pendingHydration = true
          this.setupAndRenderView().then(() => {
            this.pendingHydration = false
          })
        }
      } else {
        this.setupAndRenderView()
      }
    },
    async setupAndRenderView() {
      this.showLoadingMessage({ componentName: this.currentComponentName, text: 'Loading ...' })

      await this.setupView()

      return this.renderView()
    },

    update(selectedSearch) {
      if (this.currentComponentName) {
        render.ensureViewIsUpdatedAndRenderedImmediately(() => {
          const { currentReportCriteria, storeName } = this
          const { params, query } = currentReportCriteria || {}

          this.$store.dispatch(`${storeName}/getData`, { params, query, selectedSearch }, { root: true })
        })
      } else {
        this.$store.dispatch('messages/showLoadingMessage', { componentName: this.currentComponentName, text: 'Route Error' })
      }
    },

    async registerStore() {
      const storeMap = {
        admanager: () => import('../../admanager/store/store'),
        customAnalytics: () => import('../../custom-analytics/store/store'),
        dashboard: () => import('../../dashboard/store/store'),
        delivery: () => import('../../delivery/store/store'),
        develop: () => import('../../develop/store/store'),
        docs: () => import('../../docs/store/store'),
        home: () => import('../../home/store/store'),
        internal: () => import('../../internal/store/store'),
        programmatic: () => import('../../programmatic/store/store'),
      }

      const { storeName } = this

      if (!storeName) return this.renderError(404)

      const { default: newStore } = await storeMap[storeName]()

      return !this.isStoreRegistered && this.$store.registerModule(storeName, newStore)
    },
    setupTools({ params, query }, columns) {
      return this.$store.dispatch('tools/setup', { columns, params, query }, { root: true })
    },
    async getReportCriteria() {
      const { params, query } = this.$route
      return this.getParams({ params, query })
    },
    async setupView() {
      this.currentReportCriteria = await this.getReportCriteria()

      await this.registerStore()

      const { columns, currentReportCriteria } = this

      return this.setupTools(currentReportCriteria, columns)
    },

    renderError(errorCode) {
      const { currentComponentName, isInternalUser } = this
      const statusCode = errorCode || (isInternalUser ? 404 : 308)

      return this.showStatusMessage({ componentName: currentComponentName, statusCode })
    },
    async renderSuccess() {
      const { selectedSearch } = this

      this.update(selectedSearch)

      this.$store.dispatch('tools/showFeatureModal')

      return selectedSearch
    },
    async renderView() {
      const { associationId, currentSelectedReport, isAssociationIdRequiredOnRoute, isHomeRoute, reportPath, routeName, selectedSearch } = this

      const meetsAssociationIdRequirement = isAssociationIdRequiredOnRoute && associationId
      const errorRequest = () => meetsAssociationIdRequirement && reportPath && selectedSearch instanceof Error && 400
      // @TODO Address error cases for all-associations.
      const error404 = () => !isHomeRoute && meetsAssociationIdRequirement && reportPath && !selectedSearch && !currentSelectedReport && (associationId !== 'all-associations' || routeName === 'internal') && 404
      const errorCode = errorRequest() || error404()
      const renderError = () => errorCode && this.renderError(errorCode)

      return renderError() || this.renderSuccess()
    },
    hasChangedType(newValue, oldValue) {
      return (!newValue && !!oldValue) || (!oldValue && !!newValue)
    },
    /**
     * Compares two search options.
     * @param newValue
     * @param oldValue
     * @returns {boolean}
     */
    hasChangedSelectedOption(newValue, oldValue) {
      const diffValues = () => {
        const diffMap = [
          () => oldValue.associationId !== newValue.associationId,
          () => oldValue.baseReportName !== newValue.baseReportName,
          () => oldValue.reportPath !== newValue.reportPath,
          () => oldValue.routeName !== newValue.routeName,
          () => oldValue.subId !== newValue.subId,
        ]

        return diffMap.reduce((diffResult, diffFunction) => {
          if (diffResult !== true) {
            diffResult = diffFunction()
          }

          return diffResult
        }, false)
      }
      const hasChangedType = () => this.hasChangedType(newValue, oldValue)
      const hasChangedDeeply = () => !!newValue && !!oldValue && diffValues()

      return hasChangedType() || hasChangedDeeply()
    },
    hasChangedRouteParams(newValue, oldValue) {
      const { baseReportName: newBaseReportName, routeName: newRouteName } = newValue
      const { baseReportName: oldBaseReportName, routeName: oldRouteName } = oldValue
      return newRouteName !== oldRouteName || newBaseReportName !== oldBaseReportName
    },
    hasChangedRouteQuery(newValue, oldValue) {
      const { associationId: newAssociationId, reportPath: newReportPath, subId: newSubId } = newValue
      const { associationId: oldAssociationId, reportPath: oldReportPath, subId: oldSubId } = oldValue
      return newAssociationId !== oldAssociationId || newReportPath !== oldReportPath || newSubId !== oldSubId
    },
    hasChangedRoute(newValue, oldValue) {
      const hasChangedType = () => this.hasChangedType(newValue, oldValue)
      const hasChangedPath = () => this.hasChangedRouteParams(newValue.params, oldValue.params) || this.hasChangedRouteQuery(newValue.query, oldValue.query)

      return hasChangedType() || hasChangedPath()
    },
  },
}
</script>

<style lang="scss" scoped>
@import "~src/styles/variables";

.main-content {
  width: 100%;
  height: 100%;

  h1 {
    margin-bottom: 16px;
  }
}

.error-message,
.info-message {
  border-radius: 6px;
}

.messenger > div {
  display: flex;
  align-items: center;

  .v-icon {
    margin-right: 6px;
  }
}
</style>
